// ─── marketplaceUtils.js — pure helpers for the Marketplaces page ────────────
// Used by Marketplaces.jsx and the per-product cost editor that replaces the
// deleted Profitability page.

import { recomputeProductShares } from "../../lib/marketplaceShares.js";

// ── Sales window options ─────────────────────────────────────────────────────
// User can pick how recent the sales data is for auto-calculating product %
// of marketplace and marketplace % of total. Capped at 90 days because anything
// older is too noisy for forward-looking forecasting.
export const SALES_WINDOWS = [
  { days: 7,  label: "Last 7 days" },
  { days: 14, label: "Last 14 days" },
  { days: 30, label: "Last 30 days" },
  { days: 60, label: "Last 60 days" },
  { days: 90, label: "Last 90 days" },
];
export const DEFAULT_SALES_WINDOW = 30;

// ── Override tracking + share-recompute helpers ──────────────────────────────
// These were originally defined in this file but were promoted to
// src/lib/marketplaceShares.js so the marketplace adapters (also in src/lib/)
// can call them without crossing the page → lib boundary. We re-export from
// the new location so existing callers in this file's module path keep
// working unchanged.
export {
  isOverridden, getApiValue, applyOverride, resetField, applyApiPatch,
  recomputeShares, isMarketplaceActive, recomputeMarketplaceShares,
  recomputeProductShares,
} from "../../lib/marketplaceShares.js";

// ── Grouping helpers ─────────────────────────────────────────────────────────
// Each marketplace can have product groups (Amazon parent SKU model).
// Group shape: { id, name, productIds: string[] }
// One product can belong to ONE group max. The group's stats are the sum/avg
// of its members.

export function getProductGroup(marketplace, productId) {
  const groups = marketplace.productGroups || [];
  return groups.find(g => (g.productIds || []).includes(productId)) || null;
}

export function addProductsToGroup(marketplace, groupId, productIds) {
  const groups = marketplace.productGroups || [];
  // Remove these products from any existing groups (one parent rule)
  const cleaned = groups.map(g => ({
    ...g,
    productIds: (g.productIds || []).filter(id => !productIds.includes(id)),
  }));
  // Add to the target group
  const targetIdx = cleaned.findIndex(g => g.id === groupId);
  if (targetIdx >= 0) {
    cleaned[targetIdx] = {
      ...cleaned[targetIdx],
      productIds: [...new Set([...(cleaned[targetIdx].productIds || []), ...productIds])],
    };
  }
  return { ...marketplace, productGroups: cleaned };
}

export function createGroup(marketplace, name, productIds) {
  const id = `grp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  // Remove these products from any existing groups
  const groups = (marketplace.productGroups || []).map(g => ({
    ...g,
    productIds: (g.productIds || []).filter(pid => !productIds.includes(pid)),
  })).filter(g => (g.productIds || []).length > 0);
  groups.push({ id, name, productIds: [...productIds] });
  return { ...marketplace, productGroups: groups };
}

export function renameGroup(marketplace, groupId, newName) {
  const groups = (marketplace.productGroups || []).map(g =>
    g.id === groupId ? { ...g, name: newName } : g
  );
  return { ...marketplace, productGroups: groups };
}

export function deleteGroup(marketplace, groupId) {
  // Removes the group but keeps the products
  const groups = (marketplace.productGroups || []).filter(g => g.id !== groupId);
  return { ...marketplace, productGroups: groups };
}

// ── Aggregate group stats ────────────────────────────────────────────────────
// Returns the parent-row stats for a group: total payout, weighted % of market,
// total delivery cost, etc — weighted by REAL recent sales volume across the
// child products (industry standard). Falls back to pctOfMarket weighting
// when no sales-volume data is available, then to a flat average.
//
// The weight hierarchy is:
//   1. recentSalesVolume (units sold in the most recent window) — TRUTH
//   2. pctOfMarket (the relative share, can be manual override) — STAND-IN
//   3. flat average — last resort (no data at all)
export function aggregateGroupStats(marketplace, group) {
  const products = (marketplace.products || []).filter(p => (group.productIds || []).includes(p.id));
  if (products.length === 0) {
    return {
      productCount: 0, pctOfMarket: 0, avgPayout: 0, avgDeliveryCost: 0,
      totalPctOfMarket: 0,
    };
  }

  // Pick the best available weight per product. recentSalesVolume is the
  // persisted name; _recentSalesVolume is the legacy in-memory key (kept for
  // backward compat with sessions that haven't reloaded yet).
  const weights = products.map(p => {
    const v = Number(p.recentSalesVolume ?? p._recentSalesVolume) || 0;
    return v > 0 ? v : null;
  });
  const allHaveVolume = weights.every(w => w !== null);
  const someHaveVolume = weights.some(w => w !== null);

  let weightedPayout, weightedDelivery;
  const totalPctOfMarket = products.reduce((s, p) => s + (Number(p.pctOfMarket) || 0), 0);

  if (allHaveVolume) {
    // Industry-standard weighted average by actual unit sales volume.
    // Σ(payout × units) / Σ(units)
    const totalUnits = weights.reduce((s, w) => s + w, 0);
    weightedPayout = products.reduce((s, p, i) => s + (Number(p.payout) || 0) * weights[i], 0) / totalUnits;
    weightedDelivery = products.reduce((s, p, i) => s + (Number(p.deliveryCost) || 0) * weights[i], 0) / totalUnits;
  } else if (someHaveVolume) {
    // Mixed: use volume where available, fall back to pctOfMarket where not.
    // Normalize both contributions to a common scale (units-equivalent).
    const totalUnits = weights.reduce((s, w) => s + (w || 0), 0);
    // Treat each unknown product as if its pctOfMarket-share-of-totalUnits were its weight
    const equivalents = products.map((p, i) => {
      if (weights[i] !== null) return weights[i];
      const pct = (Number(p.pctOfMarket) || 0) / Math.max(1e-9, totalPctOfMarket);
      return totalUnits * pct;
    });
    const totalEquivalents = equivalents.reduce((s, w) => s + w, 0);
    weightedPayout = totalEquivalents > 0
      ? products.reduce((s, p, i) => s + (Number(p.payout) || 0) * equivalents[i], 0) / totalEquivalents
      : 0;
    weightedDelivery = totalEquivalents > 0
      ? products.reduce((s, p, i) => s + (Number(p.deliveryCost) || 0) * equivalents[i], 0) / totalEquivalents
      : 0;
  } else if (totalPctOfMarket > 0) {
    // No volume data anywhere — fall back to pctOfMarket weighting (legacy
    // behavior — preserves backward compat for brand-new marketplaces).
    weightedPayout  = products.reduce((s, p) => s + (Number(p.payout) || 0) * (Number(p.pctOfMarket) || 0), 0) / totalPctOfMarket;
    weightedDelivery = products.reduce((s, p) => s + (Number(p.deliveryCost) || 0) * (Number(p.pctOfMarket) || 0), 0) / totalPctOfMarket;
  } else {
    // Flat average — last resort, only when there's no signal at all
    weightedPayout  = products.reduce((s, p) => s + (Number(p.payout) || 0), 0) / products.length;
    weightedDelivery = products.reduce((s, p) => s + (Number(p.deliveryCost) || 0), 0) / products.length;
  }

  return {
    productCount: products.length,
    pctOfMarket: totalPctOfMarket,
    avgPayout: weightedPayout,
    avgDeliveryCost: weightedDelivery,
    totalPctOfMarket,
  };
}

// ── Decouple a product ───────────────────────────────────────────────────────
// Marks a product as decoupled — excluded from financial AND inventory
// calculations everywhere. The user must confirm via warning dialog before
// triggering this.
export function decoupleProduct(marketplace, productId, decoupled = true) {
  const products = (marketplace.products || []).map(p =>
    p.id === productId ? { ...p, _decoupled: decoupled } : p
  );
  return { ...marketplace, products: recomputeProductShares(products) };
}
